/**
 * Results for one sent campaign, plus the controls that still apply to it.
 *
 * The figures come from ActiveCampaign's `reportTotals` and are cached on the
 * campaign row: opens and clicks keep arriving for days after a send, so a
 * background job refreshes recent campaigns and this offers a manual refresh for
 * everything else.
 *
 * Rates are computed against messages actually sent rather than the audience
 * size, which is the honest denominator — bounces never received anything.
 */

import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../Modal';
import {
  AUDIENCE_SOURCE_LABELS,
  CAMPAIGN_STATUS_LABELS,
  campaignRates,
  controlCampaign,
  fetchCampaignRecipients,
  refreshCampaignStats,
  type AudienceSource,
  type CampaignRecipient,
  type CampaignRow,
} from '../../api/campaigns';

interface Props {
  campaign: CampaignRow;
  userId: number;
  onClose: () => void;
  onUpdated: (campaign: CampaignRow) => void;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const CampaignReportModal: React.FC<Props> = ({ campaign, userId, onClose, onUpdated }) => {
  const [row, setRow] = useState(campaign);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  /** True when the membership was read back from ActiveCampaign rather than a snapshot. */
  const [recipientsLive, setRecipientsLive] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);

  const stats = row.stats;
  const rates = campaignRates(stats);

  const apply = useCallback(
    (updated: CampaignRow) => {
      setRow(updated);
      onUpdated(updated);
    },
    [onUpdated]
  );

  // Pull fresh numbers on open. A campaign sent minutes ago has almost nothing
  // cached, and stale zeroes read as a failed send rather than a young one.
  useEffect(() => {
    if (!campaign.ac_campaign_id) return;
    let cancelled = false;
    refreshCampaignStats(campaign.id, userId)
      .then((updated) => {
        if (!cancelled) apply(updated);
      })
      .catch(() => {
        /* The cached figures are still shown; a refresh failure is not worth an alert. */
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.id, campaign.ac_campaign_id, userId, apply]);

  const run = async (label: string, action: () => Promise<CampaignRow>) => {
    setBusy(label);
    setError('');
    try {
      apply(await action());
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${label}.`);
    } finally {
      setBusy('');
    }
  };

  const loadRecipients = async () => {
    setRecipientsOpen(true);
    if (recipients.length > 0) return;
    try {
      const page = await fetchCampaignRecipients(row.id, userId);
      setRecipients(page.recipients);
      setRecipientsTotal(page.total);
      setRecipientsLive(page.live);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the recipient list.');
    }
  };

  const metrics: { label: string; value: string; hint?: string }[] = [
    { label: 'Sent', value: String(stats?.sendAmt ?? row.audience_count ?? 0) },
    {
      label: 'Opened',
      value: rates.openRate == null ? '—' : `${rates.openRate}%`,
      hint: stats?.verifiedUniqueOpens != null ? `${stats.verifiedUniqueOpens} contacts, bots excluded` : undefined,
    },
    {
      label: 'Clicked',
      value: rates.clickRate == null ? '—' : `${rates.clickRate}%`,
      hint: stats?.subscriberClicks != null ? `${stats.subscriberClicks} contacts` : undefined,
    },
    {
      label: 'Bounced',
      value: rates.bounceRate == null ? '—' : `${rates.bounceRate}%`,
      hint: stats?.totalBounces != null ? `${stats.totalBounces} addresses` : undefined,
    },
    { label: 'Unsubscribed', value: String(stats?.unsubscribes ?? 0) },
    { label: 'Replied', value: String(stats?.uniqueReplies ?? 0) },
  ];

  const canPause = row.status === 'sent' || row.status === 'scheduled';
  const canResume = row.status === 'paused';
  const canStop = row.status === 'sent' || row.status === 'paused' || row.status === 'scheduled';

  return (
    <Modal isOpen onClose={onClose} title={row.name} wide>
      <div className="modal-body campaign-report">
        <div className="campaign-report-meta">
          <span className={`campaign-status campaign-status--${row.status}`}>
            {CAMPAIGN_STATUS_LABELS[row.status]}
          </span>
          <span>{row.channel === 'sms' ? 'SMS broadcast' : 'Email campaign'}</span>
          <span>Sent {formatDateTime(row.sent_at)}</span>
          {row.sent_by_name ? <span>by {row.sent_by_name}</span> : null}
        </div>

        {row.subject ? (
          <p className="campaign-report-subject">
            <strong>Subject:</strong> {row.subject}
          </p>
        ) : null}

        {error ? (
          <p className="create-order-error" role="alert">
            {error}
          </p>
        ) : null}

        {row.last_error ? (
          <p className="create-order-error" role="alert">
            {row.last_error}
          </p>
        ) : null}

        <div className="campaign-metrics">
          {metrics.map((m) => (
            <div key={m.label} className="campaign-metric">
              <span className="campaign-metric-value">{m.value}</span>
              <span className="campaign-metric-label">{m.label}</span>
              {m.hint ? <span className="campaign-metric-hint">{m.hint}</span> : null}
            </div>
          ))}
        </div>

        <p className="campaign-hint">
          {row.stats_fetched_at
            ? `Figures from ActiveCampaign, last refreshed ${formatDateTime(row.stats_fetched_at)}.`
            : 'No figures from ActiveCampaign yet.'}
        </p>

        <div className="campaign-report-actions">
          <button
            type="button"
            className="secondary-button"
            disabled={!!busy || !row.ac_campaign_id}
            onClick={() => void run('refresh', () => refreshCampaignStats(row.id, userId))}
          >
            {busy === 'refresh' ? 'Refreshing…' : 'Refresh figures'}
          </button>
          {canPause ? (
            <button
              type="button"
              className="secondary-button"
              disabled={!!busy}
              onClick={() => void run('pause', () => controlCampaign(row.id, userId, 'pause'))}
            >
              Pause
            </button>
          ) : null}
          {canResume ? (
            <button
              type="button"
              className="secondary-button"
              disabled={!!busy}
              onClick={() => void run('resume', () => controlCampaign(row.id, userId, 'resume'))}
            >
              Resume
            </button>
          ) : null}
          {canStop ? (
            <button
              type="button"
              className="secondary-button link-button--danger"
              disabled={!!busy}
              onClick={() => {
                if (window.confirm('Stop this campaign? Delivery cannot be restarted afterwards.')) {
                  void run('stop', () => controlCampaign(row.id, userId, 'stop'));
                }
              }}
            >
              Stop
            </button>
          ) : null}
        </div>

        <div className="campaign-report-recipients">
          {recipientsOpen ? (
            <>
              <h3 className="campaign-report-subheading">Recipients ({recipientsTotal})</h3>
              <p className="campaign-hint">
                {recipientsLive
                  ? 'Read live from the ActiveCampaign list, so this is who is on it now rather than a snapshot of who was mailed.'
                  : 'Recorded as each contact was synced, so this is exactly who was mailed.'}
              </p>
              <div className="campaign-recipients-list">
                <table className="tasks-search-results-table">
                  <thead>
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={`${r.source_type}-${r.source_id}`}>
                        <td data-label="Email">{r.email}</td>
                        <td data-label="From">
                          {r.source_type === 'activecampaign_contact'
                            ? 'ActiveCampaign list'
                            : AUDIENCE_SOURCE_LABELS[r.source_type as AudienceSource] ??
                              r.source_type}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <button type="button" className="link-button" onClick={() => void loadRecipients()}>
              Show who this went to
            </button>
          )}
        </div>
      </div>

      <div className="modal-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
};

export default CampaignReportModal;
